import {
  alpha,
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemButtonProps,
  ListItemText,
  Theme,
  BoxProps,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import { PermissionGate } from "components"
import { useState, useCallback, memo, useMemo } from "react"
import { FaCircle } from "react-icons/fa6"
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md"
import { NavLink as Link, useLocation, matchPath } from "react-router-dom"

import { INavConfig } from "./NavConfig"
import { includes } from "lodash"

interface IListItemLinkProps extends ListItemButtonProps {
  active: boolean
  to: string
}

interface INavSectionProps extends BoxProps {
  navConfig: INavConfig[]
}

interface INavLinkProps {
  item: INavConfig
  active: (x: string) => boolean
  pathname: string
  level?: number
}

const ListItemLinkStyle = styled(ListItemButton)<IListItemLinkProps>(
  ({ theme, active }: { theme: Theme; active: boolean }) => ({
    height: 55,
    marginBottom: "0.3rem",
    position: "relative",
    textTransform: "capitalize",
    padding: 0,
    ...(active
      ? {
          color: theme.palette.secondary.main,
          background: alpha(theme.palette.secondary.main, 0.2),
        }
      : {}),
    "&::before": {
      content: '""',
      position: "absolute",
      left: "0",
      top: "0",
      background: theme.palette.secondary.main,
      height: "100%",
      width: active ? "4px" : "0",
    },
    "& svg": {
      color: active ? theme.palette.secondary.main : "#5e5d5d",
    },
  }),
)

const ListItemStyle = styled(ListItemButton)<{ active: boolean }>(({ theme, active }) => ({
  height: 55,
  marginBottom: "0.3rem",
  position: "relative",
  textTransform: "capitalize",
  padding: 0,
  ...(active
    ? {
        color: theme.palette.secondary.main,
        background: alpha(theme.palette.secondary.main, 0.2),
      }
    : {}),
  "&::before": {
    content: '""',
    position: "absolute",
    left: "0",
    top: "0",
    background: theme.palette.secondary.main,
    height: "100%",
    width: active ? "4px" : "0",
  },
  "& svg": {
    color: active ? theme.palette.secondary.main : "#5e5d5d",
  },
}))

const ListItemTextStyle = styled(ListItemText)({
  "& span": {
    width: "300px",
  },
})

const ListItemIconStyle = styled(ListItemIcon)({
  width: 60,
  height: 50,
  fontSize: 30,
  color: "#5e5d5d",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
})

export const NavLink = ({ active, pathname, item, level = 0 }: INavLinkProps) => {
  const isActive = useMemo(() => {
    if (item?.children) {
      return item.children.some(itemSub => active(itemSub.path))
    }

    return active(item.path)
  }, [pathname])
  const [isOpen, setIsOpen] = useState(false)

  const handleOpen = () => setIsOpen(state => !state)

  const { title, path, children, permissions, icon: Icon } = item

  if (children) {
    return (
      <>
        <PermissionGate permissions={permissions}>
          <ListItemStyle
            onClick={handleOpen}
            active={isActive ? !!String(isActive) : false}
            sx={{ pl: level < 1 ? "0.45rem" : level * 1.5 }}
          >
            {level ? (
              <ListItemIconStyle>
                <FaCircle size={7} />
              </ListItemIconStyle>
            ) : (
              <ListItemIconStyle>
                <Icon />
              </ListItemIconStyle>
            )}

            <ListItemTextStyle className="navItem" primary={title} />

            {isOpen ? <MdKeyboardArrowUp size={40} /> : <MdKeyboardArrowDown size={40} />}
          </ListItemStyle>

          <Collapse in={isOpen} unmountOnExit timeout="auto" className="collapse">
            <List disablePadding>
              {children.map(itemSub => (
                <NavLink
                  key={itemSub.title}
                  item={itemSub}
                  active={active}
                  pathname={pathname}
                  level={level === 0 ? 1 : level + 1}
                />
              ))}
            </List>
          </Collapse>
        </PermissionGate>
      </>
    )
  }

  return (
    <PermissionGate permissions={permissions}>
      <ListItemLinkStyle
        to={path}
        active={isActive ? !!String(isActive) : false}
        disableGutters
        component={Link}
        sx={{ pl: level < 1 ? "0.45rem" : level * 1.5 }}
      >
        {level ? (
          <ListItemIconStyle>
            <FaCircle size={7} />
          </ListItemIconStyle>
        ) : (
          <ListItemIconStyle>
            <Icon />
          </ListItemIconStyle>
        )}

        <ListItemTextStyle className="navItem" primary={title} />
      </ListItemLinkStyle>
    </PermissionGate>
  )
}

export const Component = ({ navConfig, ...rest }: INavSectionProps) => {
  const { pathname } = useLocation()
  const match = useCallback(
    (path: string) => {
      return path ? !!pathname.includes(path) : false
    },
    [pathname],
  )

  return (
    <Box {...rest}>
      <List>
        {navConfig.map(item => (
          <NavLink key={item.title} item={item} active={match} pathname={pathname} />
        ))}
      </List>
    </Box>
  )
}

export const NavSection = memo(Component)
